import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import { FedExIcon, USPSIcon } from '@c/icons';
import { UPS } from '@c/icons/carriers';
import AddAddressModal from '@c/modals/AddAddressModal';
import AddPaymentMethodModal from '@c/modals/AddPaymentMethodModal';
import { BProcess, ProcessStep } from '@c/process/BaseProcess';
import { useMutation, useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import SafeImage from '@ui/SafeImage';
import { calculateShipping, getShippingRegion } from '@util/firestore/cart';
import { getPaymentMethods } from '@util/firestore/payments';
import { calcTax, formatCurrency } from '@util/index';
import { getVariationPrice } from '@util/firestore/products';
import { useAuth } from 'context/AuthContext';
import ItemShippingSelect from 'features/checkout/components/ItemShippingSelect';
import { PencilIcon } from 'lucide-react';
import { ChatDocument } from 'models/chat';
import { ProductDocument, Variation } from 'models/product';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useMakeAnOffer } from '../hooks/useMakeAnOffer';
import { buildPaymentOptions } from '../utils/paymentOptions';
import { OfferSchemaType, StepType } from '../types';
import {
  validatePaymentMethod,
  validatePrice,
  validateShippingAddress,
  validateShippingOptions,
  validateTermsAndConditions,
} from '../utils';
import { isDomestic, shouldExcludeCountry } from '@util/firestore/shipengine';
interface MakeAnOfferFormProps {
  product: ProductDocument;
  selectedVariations: Variation[] | null;
  dismiss: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  stepList: StepType[];
  chat?: ChatDocument | null;
  mode?: 'make' | 'send';
}

export const MakeAnOfferForm = ({
  product,
  selectedVariations,
  dismiss,
  currentStep,
  setCurrentStep,
  stepList,
  chat,
  mode = 'make',
}: MakeAnOfferFormProps) => {
  const { userDoc } = useAuth();
  const [openModal, setOpenModal] = useState<'address' | 'payment' | ''>('');

  const [ratesError, setRatesError] = useState<Error | null>(null);

  const taxMutation = useMutation({
    mutationFn: (form: OfferSchemaType) =>
      calcTax({
        form,
        products: [product],
      }),
    onSuccess: (data) => {
      useFormReturn.setValue('tax', data);
    },
  });
  const addressOptions =
    userDoc?.addresses?.map((address) => ({
      id: `${address.address_line1}`,
      // using this validation to avoid showing empty values
      label: [
        address.address_line1,
        address.city_locality,
        address.state_province,
        address.postal_code,
      ]
        .filter(Boolean)
        .join(', '),
      value: JSON.stringify(address),
    })) ?? [];
  const { useFormReturn, getPricePercentageOff, onSubmit, onSendOffer } =
    useMakeAnOffer({
      product,
      dismiss,
      selectedVariations,
      chat,
    });

  const askingPrice = selectedVariations?.length
    ? getVariationPrice(product, selectedVariations)
    : product.price;
  const { data: paymentMethodData, isLoading: isLoadingPaymentMethods } =
    useQuery({
      queryKey: ['paymentMethods'],
      queryFn: getPaymentMethods,
      enabled: !!userDoc,
    });
  const paymentOptions = buildPaymentOptions(paymentMethodData);

  const form = useFormReturn.watch();

  useEffect(() => {
    if (userDoc?.addresses?.length && !form.shippingAddress) {
      const defaultAddress =
        userDoc.addresses.find((a) => a.is_default) || userDoc.addresses[0];
      if (defaultAddress) {
        useFormReturn.setValue(
          'shippingAddress',
          JSON.stringify(defaultAddress)
        );
        if (product.is_flat_rate) {
          const shipping = calculateShipping(
            product,
            getShippingRegion(defaultAddress)
          );
          if (shipping) {
            useFormReturn.setValue('shippingCost', shipping.cost ?? 0);
          }
        } else if (isDomestic(product.shipment, defaultAddress)) {
          useFormReturn.setValue(
            'shippingCost',
            product.shipment.domestic_flat_rate
          );
        }
      }
    }
  }, [userDoc?.addresses, form.shippingAddress, product, useFormReturn]);

  useEffect(() => {
    if (paymentOptions?.length && !form.paymentMethodId) {
      useFormReturn.setValue('paymentMethodId', paymentOptions[0].value);
    }
  }, [paymentOptions, form.paymentMethodId, useFormReturn.setValue]);

  function handleOfferClick(price: number) {
    useFormReturn.setValue('price', Number(price.toFixed(2)));
  }

  const letterSize = selectedVariations?.find(
    (v) => !v.is_number && v.size !== 'N/A'
  );
  const numberSize = selectedVariations?.find((v) => v.is_number);
  const colorVariation = selectedVariations?.find((v) => v.color);

  const steps: ProcessStep<StepType>[] = [
    {
      id: 'offer-price' as StepType,
      content: (
        <div className="flex h-full flex-col justify-between gap-[2.4rem]">
          <div className="flex flex-col gap-[2.4rem]">
            <span className="text-[2.1rem] font-semibold text-black">
              Offer Price
            </span>

            <div className="flex items-center justify-between">
              <div className="flex max-w-[30rem] items-center gap-[1.6rem]">
                <SafeImage
                  className="h-[6rem] w-[6rem] shrink-0 rounded-2xl"
                  alt={`${product.title} product image`}
                  src={product.thumbnail}
                />

                <div className="flex flex-col gap-[0.2rem]">
                  <div className="flex flex-col gap-y-2">
                    <span className="line-clamp-2 w-[22rem] text-[1.7rem] font-semibold text-black">
                      {product.title}
                    </span>

                    <span className="flex flex-col text-[1.6rem] font-medium text-[#7F7F7F]">
                      {!!selectedVariations?.length && (
                        <>
                          <span>
                            Size: {letterSize?.size ?? ''}
                            {letterSize?.size && numberSize?.size ? '/' : ''}
                            {numberSize?.size ?? ''}
                          </span>
                          {colorVariation?.color && (
                            <span>Color: {colorVariation?.color}</span>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <span className="text-[2.1rem] font-bold text-black">
                {formatCurrency(askingPrice)}
              </span>
            </div>

            <div className="border-l border-zinc-200 pl-4">
              <span className="text-[1.6rem] text-[#444444]">
                {mode === 'send'
                  ? 'Offers expire in 24 hours. If the buyer accepts, an order will be created.'
                  : 'All offers are binding and expire in 24 hours. If the seller accepts your offer, payment will be processed.'}
              </span>
            </div>

            <div className="flex flex-col items-start gap-[1.6rem]">
              <Controller
                control={useFormReturn.control}
                name={`price`}
                render={({ field: { value, onChange, onBlur } }) => (
                  <div className="w-full rounded-[1.3rem] border-[1px] border-[#D9D9D9] pb-[1rem] pl-[2rem] pr-[1rem] pt-[1rem] !text-black">
                    <FormLabel
                      labelClasses={`text-[1.8rem] mt-4 ${
                        useFormReturn.formState.errors.price
                          ? 'text-brand-secondary'
                          : '!text-black'
                      }`}
                      value={'Your offer'}
                      className="flex items-center justify-between"
                    >
                      <FormCurrencyInputFancy
                        placeholder={'0.00'}
                        error={!!useFormReturn.formState.errors.price}
                        onChange={(e) => {
                          useFormReturn.clearErrors('price');
                          onChange(e);
                        }}
                        value={value > 0 ? value.toFixed(2) : ''}
                        defaultValue={form.price.toString()}
                        onBlur={onBlur}
                        currencyInputClassName={`!text-[2rem] ${
                          useFormReturn.formState.errors.price
                            ? 'text-brand-secondary'
                            : '!text-black'
                        }`}
                      />
                    </FormLabel>
                  </div>
                )}
              />

              <div className="flex w-full items-center justify-between gap-x-4">
                {[20, 15, 10].map((percentage) => {
                  const price = getPricePercentageOff({ product, percentage, selectedVariations });
                  return (
                    <button
                      className={`flex w-full cursor-pointer flex-col items-center justify-center rounded-[1.3rem] p-4 ${
                        form.price === price
                          ? 'border border-brand-secondary bg-brand-secondary text-white'
                          : 'border border-brand-primary-lighter bg-transparent text-black hover:bg-brand-off-white'
                      }`}
                      key={percentage}
                      type="button"
                      onClick={() => handleOfferClick(price)}
                    >
                      <h1 className={'text-[2rem] font-bold'}>
                        {formatCurrency(price)}
                      </h1>

                      <span
                        className={`${
                          form.price === price
                            ? 'text-white'
                            : 'text-brand-secondary'
                        } text-[1.4rem] font-semibold`}
                      >
                        {percentage}% off
                      </span>
                    </button>
                  );
                })}
              </div>

              {useFormReturn.formState.errors.price && (
                <span className="text-[1.5rem] font-semibold text-brand-red">
                  {useFormReturn.formState.errors.price.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex w-full flex-col items-center justify-center gap-[1.6rem] gap-y-8 place-self-end">
            <div className="h-px w-full bg-zinc-200" />

            <div className="w-full pt-2">
              <div className="flex flex-col gap-2">
                <Checkbox
                  label="I acknowledge that this offer is binding and cannot be cancelled."
                  onChange={() => {
                    useFormReturn.setValue(
                      'termsAndConditions',
                      !form.termsAndConditions
                    );
                    useFormReturn.clearErrors('termsAndConditions');
                  }}
                  selected={form.termsAndConditions}
                  error={!!useFormReturn.formState.errors.termsAndConditions}
                />
                {useFormReturn.formState.errors.termsAndConditions && (
                  <span className="text-[1.5rem] font-semibold text-brand-red">
                    {useFormReturn.formState.errors.termsAndConditions.message}
                  </span>
                )}
              </div>
            </div>

            <Button
              type="secondary"
              text={mode === 'send' ? 'Send offer' : 'Continue'}
              buttonType="button"
              className="!h-[6rem] !rounded-full !py-6 !text-[1.8rem] !font-semibold"
              width="fluid"
              onClick={() => {
                if (checkErrors()) return;
                if (mode === 'send') {
                  onSendOffer();
                } else {
                  setCurrentStep(currentStep + 1);
                }
              }}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'shipping-payment' as StepType,
      content: (
        <div className="flex h-full flex-col justify-between gap-8">
          <div>
            <span className="text-[2.1rem] font-semibold text-black">
              Shipping & Payment
            </span>

            <div className="mt-8 flex flex-col gap-y-6">
              <div className="flex w-full justify-between">
                <span className="text-[1.8rem] font-semibold text-black">
                  Shipping To
                </span>

                <button
                  onClick={() => setOpenModal('address')}
                  className="pr-4 text-[1.5rem] font-medium text-brand-secondary"
                  type="button"
                >
                  Add
                </button>
              </div>

              {useFormReturn.formState.errors.shippingAddress && (
                <span className="text-[1.5rem] font-semibold text-brand-red">
                  {useFormReturn.formState.errors.shippingAddress.message}
                </span>
              )}

              <div className="px-2">
                <FormSelect
                  disabled={!addressOptions.length}
                  allowEmpty={false}
                  placeholder="Select shipping address"
                  error={!!useFormReturn.formState.errors.shippingAddress}
                  options={addressOptions}
                  rules={{ required: true }}
                  control={useFormReturn.control}
                  defaultValue={form.shippingAddress}
                  onChange={(e) => {
                    useFormReturn.setValue('rate', undefined);
                    if (product.is_flat_rate) {
                      const shipping = calculateShipping(
                        product,
                        getShippingRegion(JSON.parse(e.value))
                      );
                      if (!shipping) {
                        useFormReturn.setError('shippingAddress', {
                          type: 'manual',
                          message:
                            'No shipping options available for this product to this address',
                        });
                        return;
                      }
                      useFormReturn.setValue('shippingCost', shipping.cost);
                    } else if (
                      isDomestic(product.shipment, JSON.parse(e.value))
                    ) {
                      useFormReturn.setValue(
                        'shippingCost',
                        product.shipment.domestic_flat_rate
                      );
                    }
                    useFormReturn.clearErrors('shippingAddress');
                  }}
                  name="shippingAddress"
                />
              </div>

              <div className="mt-8 flex w-full justify-between">
                <span className="text-[1.8rem] font-semibold text-black">
                  Payment Method
                </span>

                <button
                  onClick={() => setOpenModal('payment')}
                  className="pr-4 text-[1.5rem] font-medium text-brand-secondary"
                  type="button"
                >
                  Add
                </button>
              </div>

              {useFormReturn.formState.errors.paymentMethodId && (
                <span className="text-[1.5rem] font-semibold text-brand-red">
                  {useFormReturn.formState.errors.paymentMethodId.message}
                </span>
              )}

              <div className="px-2">
                {isLoadingPaymentMethods ? (
                  <div className="flex w-full flex-col gap-4">
                    <div className="h-[5.2rem] w-full animate-pulse rounded-[1.5rem] bg-gray-200" />
                  </div>
                ) : (
                  <FormSelect
                    disabled={!paymentOptions?.length}
                    allowEmpty={false}
                    placeholder="Select payment method"
                    error={!!useFormReturn.formState.errors.paymentMethodId}
                    options={paymentOptions ?? []}
                    control={useFormReturn.control}
                    defaultValue={form.paymentMethodId}
                    name="paymentMethodId"
                  />
                )}
              </div>
            </div>
          </div>

          <div>
            <Button
              type="secondary"
              text="Continue"
              buttonType="button"
              onClick={() => {
                if (checkErrors()) return;
                taxMutation.mutate(form);
                setCurrentStep(currentStep + 1);
              }}
              className="!h-[6rem] !rounded-full !py-6 !text-[1.8rem] !font-semibold"
              width="fluid"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'shipping-options' as StepType,
      content: (
        <div className="flex h-full flex-col justify-between">
          <div className="flex flex-col gap-[2.4rem]">
            <span className="text-[2.1rem] font-semibold text-black">
              Select Shipping
            </span>

            <div className="max-h-[55rem] overflow-y-scroll">
              <div className="flex flex-col gap-[1.6rem]">
                {ratesError && (
                  <div className="flex w-full flex-col gap-4">
                    <span className="text-[1.4rem] font-semibold text-brand-red">
                      {ratesError?.message}
                    </span>
                  </div>
                )}
                {form.shippingAddress &&
                  !shouldExcludeCountry(
                    product.shipment,
                    JSON.parse(form.shippingAddress)
                  ) && (
                    <ItemShippingSelect
                      chooseCheapestByDefault
                      hideProduct
                      product={product}
                      shipment={product.shipment}
                      shipTo={JSON.parse(form.shippingAddress)}
                      offer_amount={form.price}
                      selectedRateId={form.rate?.rate_id ?? null}
                      onOptionSelected={({ rate }) => {
                        useFormReturn.setValue('rate', {
                          rate_id: rate.rate_id,
                          total_amount: rate.total_amount,
                          carrier_code: rate.carrier_code,
                          delivery_days: rate.delivery_days,
                          carrier_delivery_days: rate.carrier_delivery_days,
                          service_type: rate.service_type,
                        });

                        useFormReturn.setValue(
                          'shippingCost',
                          rate.total_amount
                        );
                      }}
                      onError={(error) => {
                        setRatesError(error);
                      }}
                    />
                  )}
                {form.shippingAddress &&
                  shouldExcludeCountry(
                    product.shipment,
                    JSON.parse(form.shippingAddress)
                  ) && (
                    <div className="flex w-full flex-col gap-4">
                      <span className="text-[1.4rem] font-semibold text-brand-red">
                        Shipping to this region is not available. Please contact
                        the seller for more information.
                      </span>
                    </div>
                  )}
              </div>
            </div>
          </div>

          <Button
            type="secondary"
            buttonType="button"
            onClick={() => {
              if (checkErrors()) return;
              taxMutation.mutate(form);
              setCurrentStep(currentStep + 1);
            }}
            className="mt-4 !h-[6rem] !rounded-full !py-6 !text-[1.8rem] !font-semibold"
            width="fluid"
          >
            <>
              {form.rate ? (
                <div className="flex items-center gap-x-4">
                  <span className="text-[1.8rem] font-semibold text-white">
                    Continue with
                  </span>

                  {form.rate?.carrier_code === 'fedex' ? (
                    <FedExIcon className="h-10 w-10" />
                  ) : form.rate?.carrier_code === 'ups' ? (
                    <UPS className="h-10 w-10" />
                  ) : (
                    <USPSIcon className="h-10 w-10" />
                  )}

                  <span className="text-[1.8rem] font-semibold text-white">
                    {form.rate?.delivery_days ??
                      form.rate?.carrier_delivery_days}{' '}
                    {(form.rate?.delivery_days ??
                      form.rate?.carrier_delivery_days) === 1
                      ? 'Day'
                      : 'Days'}{' '}
                    ({formatCurrency(form.rate?.total_amount ?? 0)})
                  </span>
                </div>
              ) : isDomestic(
                  product.shipment,
                  JSON.parse(form.shippingAddress ?? '{}')
                ) ? (
                <div className="flex items-center gap-x-4">
                  <span className="text-[1.8rem] font-semibold text-white">
                    Continue
                  </span>
                </div>
              ) : (
                <span className="text-[1.8rem] font-semibold text-white">
                  Please select a shipping option
                </span>
              )}
            </>
          </Button>
        </div>
      ),
    },
    {
      id: 'review-offer' as StepType,
      content: (
        <div className="flex h-full flex-col">
          <div className="flex flex-1 flex-col gap-[2.4rem]">
            <div className="flex w-full items-center justify-between">
              <span className="text-[2.1rem] font-semibold text-black">
                Review Offer
              </span>

              <button
                type="button"
                onClick={() => {
                  setCurrentStep(
                    stepList.findIndex((step) => step === 'offer-price')
                  );
                }}
                className="pr-4 text-[1.8rem] font-medium text-brand-secondary"
              >
                Edit
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex max-w-[30rem] items-center gap-[1.6rem]">
                <SafeImage
                  className="h-[6rem] w-[6rem] shrink-0 rounded-2xl"
                  alt={'product image'}
                  src={product.thumbnail}
                />

                <div className="flex flex-col gap-[0.2rem]">
                  <div className="flex flex-col gap-y-2">
                    <span className="w-[22rem] truncate text-[1.7rem] font-semibold text-black">
                      {product.title}
                    </span>

                    <span className="flex flex-col text-[1.6rem] font-medium text-[#7F7F7F]">
                      {!!selectedVariations?.length && (
                        <>
                          <span>
                            Size: {letterSize?.size ?? ''}
                            {letterSize?.size && numberSize?.size ? '/' : ''}
                            {numberSize?.size ?? ''}
                          </span>
                          {colorVariation?.color && (
                            <span>Color: {colorVariation?.color}</span>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-x-4">
                <span className="text-[2.1rem] font-bold text-black">
                  {formatCurrency(form.price)}
                </span>
              </div>
            </div>

            <div className="h-px w-full bg-zinc-200" />

            <div className="flex flex-col gap-y-6">
              <div className="flex w-full justify-between">
                <span className="text-[1.8rem] font-semibold text-black">
                  Shipping To
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setCurrentStep(
                      stepList.findIndex((step) => step === 'shipping-payment')
                    )
                  }
                  className="pr-4 text-[1.8rem] font-medium text-brand-secondary"
                >
                  Edit
                </button>
              </div>

              {form.shippingAddress ? (
                <div className="flex items-center gap-x-4">
                  <div className="flex items-center gap-x-2">
                    <span className="text-[1.8rem] font-medium text-black">
                      {JSON.parse(form.shippingAddress).address_line1},{' '}
                      {JSON.parse(form.shippingAddress).city_locality},{' '}
                      {JSON.parse(form.shippingAddress).state_province}{' '}
                      {JSON.parse(form.shippingAddress).postal_code}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="text-[1.5rem] font-semibold text-brand-red">
                  Please select a shipping address
                </span>
              )}

              <div className="mt-8 flex w-full justify-between">
                <span className="text-[1.8rem] font-semibold text-black">
                  Payment Method
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setCurrentStep(
                      stepList.findIndex((step) => step === 'shipping-payment')
                    )
                  }
                  className="pr-4 text-[1.8rem] font-medium text-brand-secondary"
                >
                  Edit
                </button>
              </div>

              {form.paymentMethodId &&
              paymentMethodData?.find(
                (pm) => pm.id === form.paymentMethodId
              ) ? (
                <div className="flex items-center gap-x-4">
                  <SafeImage
                    src={`/cc_brands/${paymentMethodData
                      .find((pm) => pm.id === form.paymentMethodId)
                      ?.brand?.toLowerCase()}.png`}
                    alt={
                      paymentMethodData.find(
                        (pm) => pm.id === form.paymentMethodId
                      )?.brand ?? ''
                    }
                    width={32}
                    height={20}
                    className="mr-2 h-20 w-20 object-contain"
                  />

                  <span className="text-[1.8rem] font-medium text-black">
                    {`Card ending in ${
                      paymentMethodData.find(
                        (pm) => pm.id === form.paymentMethodId
                      )?.last4
                    }`}
                  </span>
                </div>
              ) : (
                <span className="text-[1.5rem] font-semibold text-brand-red">
                  Please select a payment method
                </span>
              )}

              <div className="mt-4 h-px w-full bg-zinc-200" />

              <div className="flex flex-col gap-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    Offer Price
                  </span>
                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    {formatCurrency(form.price)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="flex gap-x-4 text-[1.8rem] font-medium text-[#444444]">
                    Shipping{' '}
                    {form.rate && (
                      <div className="flex items-center gap-x-4">
                        {form.rate.carrier_code === 'fedex' ? (
                          <FedExIcon className="h-8 w-8" />
                        ) : form.rate.carrier_code === 'ups' ? (
                          <UPS className="h-8 w-8" />
                        ) : (
                          <USPSIcon className="h-8 w-8" />
                        )}

                        <div className="flex grow items-center gap-5">
                          <h5 className="text-[1.6rem] font-semibold text-brand-secondary">
                            {form.rate.delivery_days ??
                              form.rate.carrier_delivery_days}{' '}
                            {(form.rate.delivery_days ??
                              form.rate.carrier_delivery_days) === 1
                              ? 'Day'
                              : 'Days'}
                          </h5>
                        </div>
                        <button
                          onClick={() => {
                            setCurrentStep(
                              stepList.findIndex(
                                (step) => step === 'shipping-options'
                              )
                            );
                          }}
                          type="button"
                          className="text-[1.8rem] font-medium text-brand-secondary"
                        >
                          <PencilIcon className="h-8 w-8" />
                        </button>
                      </div>
                    )}
                  </span>

                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    {formatCurrency(form.shippingCost)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[1.8rem] font-medium text-[#444444]">
                    Tax
                  </span>

                  {taxMutation.isPending ? (
                    <span className="h-10 w-20 animate-pulse rounded-full bg-gray-200 text-[1.8rem] font-medium"></span>
                  ) : taxMutation.error ? (
                    <span className="text-[1.8rem] font-medium text-brand-red">
                      {taxMutation.error.message}
                    </span>
                  ) : (
                    <span className="text-[1.8rem] font-medium text-[#444444]">
                      {formatCurrency(form.tax)}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[1.8rem] font-bold text-black">
                    You will pay
                  </span>
                  {taxMutation.isPending ? (
                    <span className="h-10 w-20 animate-pulse rounded-full bg-gray-200 text-[1.8rem] font-medium"></span>
                  ) : taxMutation.error ? (
                    <span className="text-[1.8rem] font-medium text-brand-red">
                      {taxMutation.error.message}
                    </span>
                  ) : (
                    <span className="text-[2rem] font-bold text-black">
                      {formatCurrency(
                        form.price + form.shippingCost + form.tax
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {useFormReturn.formState.errors.root && (
            <span className="text-center text-[1.5rem] font-semibold text-brand-red">
              {useFormReturn.formState.errors.root.message}
            </span>
          )}
          <div className="mt-8 pb-8">
            <Button
              type="secondary"
              text={'Submit Offer'}
              onClick={() => {
                useFormReturn.handleSubmit(onSubmit)();
              }}
              width="fluid"
              loading={useFormReturn.formState.isSubmitting}
              className="!h-[6rem] !rounded-full !py-6 !text-[1.8rem] !font-semibold"
            />
          </div>
        </div>
      ),
    },
  ].filter((step) => stepList.includes(step.id));

  const checkErrors = () => {
    let hasErrors = false;
    switch (steps[currentStep].id) {
      case 'offer-price':
        validatePrice({ form, product, selectedVariations }).forEach((error) => {
          hasErrors = true;
          useFormReturn.setError(
            'price',
            {
              type: 'manual',
              message: error,
            },
            {
              shouldFocus: true,
            }
          );
        });
        validateTermsAndConditions(form).forEach((error) => {
          hasErrors = true;
          useFormReturn.setError(
            'termsAndConditions',
            {
              type: 'manual',
              message: error,
            },
            {
              shouldFocus: true,
            }
          );
        });
        break;
      case 'shipping-payment':
        validateShippingAddress(form).forEach((error) => {
          hasErrors = true;
          useFormReturn.setError(
            'shippingAddress',
            {
              type: 'manual',
              message: error,
            },
            {
              shouldFocus: true,
            }
          );
        });
        validatePaymentMethod(form).forEach((error) => {
          hasErrors = true;
          useFormReturn.setError(
            'paymentMethodId',
            {
              type: 'manual',
              message: error,
            },
            {
              shouldFocus: true,
            }
          );
        });
        break;
      case 'shipping-options':
        validateShippingOptions({ form, product }).forEach((error) => {
          hasErrors = true;
          useFormReturn.setError(
            'rate',
            {
              type: 'manual',
              message: error,
            },
            { shouldFocus: true }
          );
        });
    }
    return hasErrors;
  };
  return (
    <>
      <form
        onSubmit={useFormReturn.handleSubmit(onSubmit)}
        className="flex h-full w-full justify-center"
      >
        <BProcess steps={steps} currentStep={currentStep} />
      </form>

      {openModal === 'address' && (
        <AddAddressModal
          isOpen={true}
          dismiss={(address) => {
            setOpenModal('');

            if (address) {
              useFormReturn.setValue(
                'shippingAddress',
                JSON.stringify(address)
              );
            }
          }}
        />
      )}

      {openModal === 'payment' && (
        <AddPaymentMethodModal
          isOpen={true}
          dismiss={(paymentMethod) => {
            setOpenModal('');
            if (paymentMethod) {
              useFormReturn.setValue('paymentMethodId', paymentMethod);
            }
          }}
        />
      )}
    </>
  );
};
